// Spelled with the extension so this file can be mirrored into the test suite —
// Node cannot resolve an extensionless relative import and the mirror refuses to
// guess. Webpack resolves it either way.
import { getJobStatusUI } from './jobStatus.js';

// =====================
// helpers
// =====================
const shortId = (id, left = 6, right = 4) => {
  if (!id) return '-';
  if (id.length <= left + right + 3) return id;
  return `${id.slice(0, left)}...${id.slice(-right)}`;
};

const getTotalTasks = job => {
  return Object.keys(job?.tasks || {}).length;
};

const getTokenProgress = job => {
  const tokens = Object.values(job?.tokens || {});
  const max = tokens.length || 1;

  const min = tokens.filter(
    t =>
      (typeof t.producedLength === 'number' && t.producedLength > 0) ||
      t.status === 'EXECUTED' ||
      t.status === 'DONE'
  ).length;

  return { min, max };
};

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** The machines this product is routed across, in no particular order. */
const getMachines = job => [
  ...new Set(
    Object.values(job?.tasks || {})
      .map(t => t?.machineId)
      .filter(Boolean)
      .map(String)
  )
];

/**
 * Where the product is going, which is as close to a "client" as a JOB gets.
 *
 * There is no customer name on a job document — the allocation lives on the
 * ORDER, and a product can be split across several of them. What the job does
 * carry is the split it was launched with: how many metres were promised to
 * orders and how many are going to stock. That is the question somebody
 * scanning these cards is actually asking, and unlike a name it is here.
 */
const getDestination = job => {
  const launch = job?.source?.launch || {};
  const toOrders = num(launch.lengthToOrders);
  const toStock = num(launch.lengthToStock);
  const kind = String(launch.sourceKind || job?.source?.kind || '').toUpperCase();

  // `sourceKind` says MIXED on products that are entirely for stock, so the
  // metres decide and the stored kind is only the fallback.
  let label = kind || '—';
  if (toOrders > 0 && toStock > 0) label = 'ORDERS + STOCK';
  else if (toOrders > 0) label = 'ORDERS';
  else if (toStock > 0) label = 'STOCK';

  return { label, toOrders, toStock, kind };
};

// =====================
// MAIN ADAPTER
// =====================
export const adaptJobToProjectRow = job => {
  const tokens = Object.values(job?.tokens || {});
  const dueDate = job?.source?.launch?.dueDate || job?.source?.dueDate || null;

  return {
    // 🔑 IDs
    jobId: job.jobId,
    jobIdShort: shortId(job.jobId),
    standardId: job.standardId,
    jobCode: job.jobCode,
    plannedLength: job.plannedLength || 0,
    // 📦 Project name
    name: job.name,
    numberOfConductors: job.numberOfConductors,
    section: job.section,
    greenYellowSection: job.greenYellowSection ?? null,
    productLabel: job.productLabel || 'N/A',

    // 📅 Dates
    start: job.createdAt ? new Date(job.createdAt).toLocaleString() : '-',
    // The launch's own due date. This used to be a hard-coded '-' with a note
    // saying it would be worked out later; it was on the job all along.
    deadline: dueDate ? new Date(dueDate).toLocaleDateString('ro-RO') : '-',
    dueDate,

    // 🔢 Metrics
    task: getTotalTasks(job),
    progressTokens: getTokenProgress(job),

    // 🔢 …and the rest of what a card or a row can show without a second fetch
    executedLength: num(job.executedLength),
    progress: num(job.progress),
    drums: tokens.length,
    phases: Object.keys(job?.phases || {}),
    machines: getMachines(job),
    destination: getDestination(job),
    createdBy: job.createdBy || null,

    // 🚦 Status UI
    status: getJobStatusUI(job.status)
  };
};
